import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  AccountBalance,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../contexts/LanguageContext';
import { accountStatementService } from '../../services/accountStatement.service';
import { AccountStatementDto } from '../../types/accountStatement.types';
import { CostCenterDto } from '../../types/costCenters.types';
import { ChartOfAccount } from '../../types/chartOfAccount';
import { costCentersService } from '../../services/costCenters.service';
import { chartOfAccountService } from '../../services/chartOfAccount.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';

const translations = {
  en: {
    title: 'Account Statement',
    account: 'Account',
    costCenter: 'Cost Center',
    allBranches: 'All Branches',
    fromDate: 'From Date',
    toDate: 'To Date',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    loading: 'Loading...',
    noData: 'No data available',
    date: 'Date',
    entryNo: 'Entry No.',
    description: 'Description',
    debit: 'Debit',
    credit: 'Credit',
    balance: 'Balance',
    openingBalance: 'Opening Balance',
    closingBalance: 'Closing Balance',
    period: 'Period',
    selectAccount: 'Select Account',
  },
  ar: {
    title: 'كشف حساب',
    account: 'الحساب',
    costCenter: 'مركز التكلفة',
    allBranches: 'جميع الفروع',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    loading: 'جاري التحميل...',
    noData: 'لا توجد بيانات',
    date: 'التاريخ',
    entryNo: 'رقم القيد',
    description: 'الوصف',
    debit: 'مدين',
    credit: 'دائن',
    balance: 'الرصيد',
    openingBalance: 'الرصيد الافتتاحي',
    closingBalance: 'الرصيد الختامي',
    period: 'الفترة',
    selectAccount: 'اختر الحساب',
  },
};

const AccountStatementReport: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const t = translations[language];
  const { minDate, maxDate } = useFiscalDateRange();

  const [reportData, setReportData] = useState<AccountStatementDto | null>(null);
  const [costCenters, setCostCenters] = useState<CostCenterDto[]>([]);
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);

  const [selectedAccount, setSelectedAccount] = useState<ChartOfAccount | null>(null);
  const [selectedCostCenter, setSelectedCostCenter] = useState<number | null>(null);
  const [fromDate, setFromDate] = useState<string>(
    new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]
  );
  const [toDate, setToDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );

  useEffect(() => {
    loadCostCenters();
    loadAccounts();
    loadCompanyInfo();
  }, []);

  const loadCostCenters = async () => {
    try {
      const data = await costCentersService.getAll();
      setCostCenters(data.filter(cc => cc.isActive));
    } catch (error) {
      console.error('Failed to load cost centers:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await chartOfAccountService.getAll();
      setAccounts(data);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadReport = async () => {
    if (!selectedAccount) {
      toast.error(t.selectAccount);
      return;
    }

    try {
      setLoading(true);
      const data = await accountStatementService.getAccountStatement(
        selectedAccount.accountId,
        selectedCostCenter,
        fromDate,
        toDate
      );
      setReportData(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load report');
      console.error('Failed to load report:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(Math.abs(value));
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const getCostCenterName = (): string => {
    if (!selectedCostCenter) return t.allBranches;
    const cc = costCenters.find(c => c.costCenterId === selectedCostCenter);
    return cc ? (isRTL ? cc.costCenterNameAr : cc.costCenterName) : '';
  };

  const exportToExcel = async () => {
    if (!reportData) return;

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      let currentRow = 1;

      // Logo
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 80, height: 60 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      // Company Info
      worksheet.mergeCells('B1:F1');
      const companyCell = worksheet.getCell('B1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 18, bold: true, color: { argb: 'FFFF6B35' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('B2:F2');
      const addressCell = worksheet.getCell('B2');
      addressCell.value = `📍 ${companyInfo?.address || ''}`;
      addressCell.font = { size: 9, color: { argb: 'FF424242' } };
      addressCell.alignment = { horizontal: 'center' };

      currentRow = 5;

      // Title
      worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
      const titleCell = worksheet.getCell(`A${currentRow}`);
      titleCell.value = t.title;
      titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFF6B35' }
      };

      currentRow++;

      // Account & Period Info
      worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
      const accountCell = worksheet.getCell(`A${currentRow}`);
      accountCell.value = `${t.account}: ${isRTL ? reportData.accountNameAr : reportData.accountName} (${reportData.accountCode})`;
      accountCell.font = { size: 11, bold: true, color: { argb: 'FF2196F3' } };
      accountCell.alignment = { horizontal: 'center' };

      currentRow++;

      worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
      const branchCell = worksheet.getCell(`A${currentRow}`);
      branchCell.value = `${t.costCenter}: ${getCostCenterName()}`;
      branchCell.font = { size: 10, color: { argb: 'FF424242' } };
      branchCell.alignment = { horizontal: 'center' };

      currentRow++;

      worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
      const periodCell = worksheet.getCell(`A${currentRow}`);
      periodCell.value = `${t.period}: ${formatDate(fromDate)} - ${formatDate(toDate)}`;
      periodCell.font = { size: 10, color: { argb: 'FF424242' } };
      periodCell.alignment = { horizontal: 'center' };

      currentRow += 2;

      // Headers
      const headers = [t.date, t.entryNo, t.description, t.debit, t.credit, t.balance];
      const headerColors = ['FF4A90E2', 'FF4A90E2', 'FF4A90E2', 'FF4CAF50', 'FFF44336', 'FFFF9800'];
      
      headers.forEach((header, index) => {
        const cell = worksheet.getCell(currentRow, index + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: headerColors[index] } };
        cell.alignment = { horizontal: 'center' };
      });

      currentRow++;

      // Opening Balance
      worksheet.getCell(currentRow, 1).value = '';
      worksheet.getCell(currentRow, 2).value = '';
      const obCell = worksheet.getCell(currentRow, 3);
      obCell.value = t.openingBalance;
      obCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      obCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF424242' } };
      
      worksheet.getCell(currentRow, 4).value = '';
      worksheet.getCell(currentRow, 5).value = '';
      
      const obBalCell = worksheet.getCell(currentRow, 6);
      obBalCell.value = formatCurrency(reportData.openingBalance);
      obBalCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      obBalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF424242' } };
      obBalCell.alignment = { horizontal: 'right' };

      currentRow++;

      // Details
      reportData.details.forEach((detail) => {
        worksheet.getCell(currentRow, 1).value = formatDate(detail.date);
        worksheet.getCell(currentRow, 2).value = detail.entryNo;
        worksheet.getCell(currentRow, 3).value = detail.description;
        
        worksheet.getCell(currentRow, 4).value = detail.debit > 0 ? formatCurrency(detail.debit) : '-';
        worksheet.getCell(currentRow, 4).alignment = { horizontal: 'right' };
        
        worksheet.getCell(currentRow, 5).value = detail.credit > 0 ? formatCurrency(detail.credit) : '-';
        worksheet.getCell(currentRow, 5).alignment = { horizontal: 'right' };
        
        worksheet.getCell(currentRow, 6).value = formatCurrency(detail.balance);
        worksheet.getCell(currentRow, 6).alignment = { horizontal: 'right' };
        
        currentRow++;
      });

      // Closing Balance
      worksheet.getCell(currentRow, 1).value = '';
      worksheet.getCell(currentRow, 2).value = '';
      const cbCell = worksheet.getCell(currentRow, 3);
      cbCell.value = t.closingBalance;
      cbCell.font = { bold: true, color: { argb: 'FF000000' } };
      cbCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF9800' } };
      
      const cbDebitCell = worksheet.getCell(currentRow, 4);
      cbDebitCell.value = formatCurrency(reportData.totalDebit);
      cbDebitCell.font = { bold: true };
      cbDebitCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF9800' } };
      cbDebitCell.alignment = { horizontal: 'right' };
      
      const cbCreditCell = worksheet.getCell(currentRow, 5);
      cbCreditCell.value = formatCurrency(reportData.totalCredit);
      cbCreditCell.font = { bold: true };
      cbCreditCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF9800' } };
      cbCreditCell.alignment = { horizontal: 'right' };
      
      const cbBalCell = worksheet.getCell(currentRow, 6);
      cbBalCell.value = formatCurrency(reportData.closingBalance);
      cbBalCell.font = { bold: true };
      cbBalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF9800' } };
      cbBalCell.alignment = { horizontal: 'right' };

      // Column widths
      worksheet.getColumn(1).width = 12;
      worksheet.getColumn(2).width = 15;
      worksheet.getColumn(3).width = 40;
      worksheet.getColumn(4).width = 15;
      worksheet.getColumn(5).width = 15;
      worksheet.getColumn(6).width = 15;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t.title}_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success(t.exportExcel + ' ✔');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(isRTL ? 'فشل التصدير' : 'Export failed');
    }
  };

  const exportToPdf = async () => {
    if (!reportData) return;

    try {
      const html2canvas = (await import('html2canvas')).default;
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPosition = 20;

      // Logo
      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', pageWidth / 2 - 15, 10, 30, 20);
          yPosition = 35;
        } catch (err) {
          console.warn('Could not add logo to PDF:', err);
        }
      }

      // Header
      const headerDiv = document.createElement('div');
      headerDiv.style.cssText = 'position: absolute; left: -9999px; width: 180mm; padding: 20px; text-align: center;';
      headerDiv.innerHTML = `
        <div style="font-size: 20px; font-weight: bold; color: #000; margin-bottom: 10px;">
          ${isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
        </div>
        <div style="font-size: 12px; color: #000; margin-bottom: 15px;">
          ${companyInfo?.address || ''}
        </div>
        <div style="background: #FF6B35; color: white; padding: 12px; font-size: 16px; font-weight: bold; margin-bottom: 15px;">
          ${t.title}
        </div>
        <div style="background: #E3F2FD; padding: 10px; margin-bottom: 10px;">
          <div style="color: #000; font-weight: bold; font-size: 13px; margin-bottom: 5px;">
            ${t.account}: ${isRTL ? reportData.accountNameAr : reportData.accountName} (${reportData.accountCode})
          </div>
          <div style="color: #000; font-size: 11px; margin-bottom: 3px;">
            ${t.costCenter}: ${getCostCenterName()}
          </div>
          <div style="color: #000; font-size: 11px;">
            ${t.period}: ${formatDate(fromDate)} - ${formatDate(toDate)}
          </div>
        </div>
      `;
      document.body.appendChild(headerDiv);

      const headerCanvas = await html2canvas(headerDiv, { scale: 2, useCORS: true, logging: false });
      document.body.removeChild(headerDiv);

      const headerImgData = headerCanvas.toDataURL('image/png');
      const headerHeight = (headerCanvas.height * pageWidth) / headerCanvas.width;
      doc.addImage(headerImgData, 'PNG', 10, yPosition, pageWidth - 20, headerHeight);
      yPosition += headerHeight + 5;

      // Table
      const tableDiv = document.createElement('div');
      tableDiv.style.cssText = 'position: absolute; left: -9999px; width: 180mm; background: white;';
      
      let tableHTML = `
        <table style="width: 100%; border-collapse: collapse; font-family: Arial;">
          <thead>
            <tr>
              <th style="background: #4A90E2; color: white; border: 1px solid #ddd; padding: 8px;">${t.date}</th>
              <th style="background: #4A90E2; color: white; border: 1px solid #ddd; padding: 8px;">${t.entryNo}</th>
              <th style="background: #4A90E2; color: white; border: 1px solid #ddd; padding: 8px;">${t.description}</th>
              <th style="background: #4CAF50; color: white; border: 1px solid #ddd; padding: 8px;">${t.debit}</th>
              <th style="background: #F44336; color: white; border: 1px solid #ddd; padding: 8px;">${t.credit}</th>
              <th style="background: #FF9800; color: white; border: 1px solid #ddd; padding: 8px;">${t.balance}</th>
            </tr>
          </thead>
          <tbody>
            <tr style="background: #424242; color: white;">
              <td style="border: 1px solid #ddd; padding: 6px;"></td>
              <td style="border: 1px solid #ddd; padding: 6px;"></td>
              <td style="border: 1px solid #ddd; padding: 6px; font-weight: bold;">${t.openingBalance}</td>
              <td style="border: 1px solid #ddd; padding: 6px;"></td>
              <td style="border: 1px solid #ddd; padding: 6px;"></td>
              <td style="border: 1px solid #ddd; padding: 6px; text-align: right; font-weight: bold;">${formatCurrency(reportData.openingBalance)}</td>
            </tr>
      `;

      reportData.details.forEach((detail) => {
        tableHTML += `
          <tr>
            <td style="border: 1px solid #ddd; padding: 6px; color: #000;">${formatDate(detail.date)}</td>
            <td style="border: 1px solid #ddd; padding: 6px; color: #000;">${detail.entryNo}</td>
            <td style="border: 1px solid #ddd; padding: 6px; color: #000; text-align: ${isRTL ? 'right' : 'left'};">${detail.description}</td>
            <td style="border: 1px solid #ddd; padding: 6px; color: #000; text-align: right;">${detail.debit > 0 ? formatCurrency(detail.debit) : '-'}</td>
            <td style="border: 1px solid #ddd; padding: 6px; color: #000; text-align: right;">${detail.credit > 0 ? formatCurrency(detail.credit) : '-'}</td>
            <td style="border: 1px solid #ddd; padding: 6px; color: #000; text-align: right;">${formatCurrency(detail.balance)}</td>
          </tr>
        `;
      });

      tableHTML += `
            <tr style="background: #FF9800;">
              <td style="border: 1px solid #ddd; padding: 6px;"></td>
              <td style="border: 1px solid #ddd; padding: 6px;"></td>
              <td style="border: 1px solid #ddd; padding: 6px; font-weight: bold; color: #000;">${t.closingBalance}</td>
              <td style="border: 1px solid #ddd; padding: 6px; font-weight: bold; color: #000; text-align: right;">${formatCurrency(reportData.totalDebit)}</td>
              <td style="border: 1px solid #ddd; padding: 6px; font-weight: bold; color: #000; text-align: right;">${formatCurrency(reportData.totalCredit)}</td>
              <td style="border: 1px solid #ddd; padding: 6px; font-weight: bold; color: #000; text-align: right;">${formatCurrency(reportData.closingBalance)}</td>
            </tr>
          </tbody>
        </table>
      `;

      tableDiv.innerHTML = tableHTML;
      document.body.appendChild(tableDiv);

      const canvas = await html2canvas(tableDiv, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
      document.body.removeChild(tableDiv);

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      doc.addImage(imgData, 'PNG', 10, yPosition, imgWidth, imgHeight);

      doc.save(`${t.title}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(t.exportPdf + ' ✔');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error(isRTL ? 'فشل تصدير PDF' : 'PDF export failed');
    }
  };

  const handlePrint = () => {
    if (!reportData) return;

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}">
<head><title>${t.title}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 20px; font-size: 11px; color: #000; }
  .header { text-align: center; margin-bottom: 15px; }
  .header img { max-height: 50px; margin-bottom: 8px; display: block; margin-left: auto; margin-right: auto; }
  .header h1 { margin: 3px 0; font-size: 16px; }
  .header h2 { margin: 3px 0; font-size: 14px; color: #FF6B35; }
  .info-box { background: #E3F2FD; padding: 10px; margin-bottom: 15px; text-align: center; border-radius: 4px; }
  .info-box p { margin: 3px 0; font-size: 11px; }
  .info-box .account-name { font-size: 13px; font-weight: bold; color: #1565C0; }
  table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: ${isRTL ? 'right' : 'left'}; }
  th { font-size: 10px; font-weight: bold; color: #fff; }
  th.date-col  { background: #4A90E2; }
  th.entry-col { background: #4A90E2; }
  th.desc-col  { background: #4A90E2; }
  th.debit-col { background: #4CAF50; }
  th.cred-col  { background: #F44336; }
  th.bal-col   { background: #FF9800; }
  .num { text-align: right; font-family: monospace; }
  .row-opening { background: #424242; color: #fff; font-weight: bold; }
  .row-closing { background: #FF9800; font-weight: bold; }
  .row-odd { background: #fafafa; }
  @media print { body { margin: 10px; } @page { margin: 12mm; size: A4 portrait; } }
</style></head>
<body>
  <div class="header">
    ${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}" />` : ''}
    <h1>${isRTL ? (companyInfo?.companyNameAr || companyInfo?.companyName || '') : (companyInfo?.companyName || '')}</h1>
    ${companyInfo?.address ? `<p style="color:#555;font-size:11px;">${companyInfo.address}</p>` : ''}
    <h2>${t.title}</h2>
  </div>
  <div class="info-box">
    <p class="account-name">${t.account}: ${isRTL ? reportData.accountNameAr : reportData.accountName} (${reportData.accountCode})</p>
    <p>${t.costCenter}: ${getCostCenterName()}</p>
    <p>${t.period}: ${formatDate(fromDate)} — ${formatDate(toDate)}</p>
  </div>
  <table>
    <thead>
      <tr>
        <th class="date-col">${t.date}</th>
        <th class="entry-col">${t.entryNo}</th>
        <th class="desc-col">${t.description}</th>
        <th class="debit-col num">${t.debit}</th>
        <th class="cred-col num">${t.credit}</th>
        <th class="bal-col num">${t.balance}</th>
      </tr>
    </thead>
    <tbody>
      <tr class="row-opening">
        <td colspan="5">${t.openingBalance}</td>
        <td class="num">${formatCurrency(reportData.openingBalance)}</td>
      </tr>
      ${reportData.details.map((d, i) => `
      <tr${i % 2 === 1 ? ' class="row-odd"' : ''}>
        <td>${formatDate(d.date)}</td>
        <td>${d.entryNo}</td>
        <td>${d.description}</td>
        <td class="num">${d.debit > 0 ? formatCurrency(d.debit) : '-'}</td>
        <td class="num">${d.credit > 0 ? formatCurrency(d.credit) : '-'}</td>
        <td class="num">${formatCurrency(d.balance)}</td>
      </tr>`).join('')}
      <tr class="row-closing">
        <td colspan="3">${t.closingBalance}</td>
        <td class="num">${formatCurrency(reportData.totalDebit)}</td>
        <td class="num">${formatCurrency(reportData.totalCredit)}</td>
        <td class="num">${formatCurrency(reportData.closingBalance)}</td>
      </tr>
    </tbody>
  </table>
</body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => { w.print(); w.close(); }, 400); }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AccountBalance sx={{ fontSize: 40, color: '#FF6B35' }} />
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#FF6B35' }}>
            {t.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadReport} sx={{ color: '#FF6B35' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.exportExcel}>
            <IconButton onClick={exportToExcel} sx={{ color: '#4CAF50' }}>
              <ExcelIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.exportPdf}>
            <IconButton onClick={exportToPdf} sx={{ color: '#F44336' }}>
              <PdfIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.print}>
            <IconButton onClick={handlePrint} sx={{ color: '#2196F3' }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
          <Autocomplete
            options={accounts}
            getOptionLabel={(option) => isRTL ? `${option.accountCode} - ${option.accountNameAr}` : `${option.accountCode} - ${option.accountName}`}
            value={selectedAccount}
            onChange={(_, newValue) => setSelectedAccount(newValue)}
            renderInput={(params) => <TextField {...params} label={t.account} size="small" />}
          />

          <TextField
            fullWidth
            select
            size="small"
            label={t.costCenter}
            value={selectedCostCenter || ''}
            onChange={(e) => setSelectedCostCenter(e.target.value ? Number(e.target.value) : null)}
          >
            <MenuItem value="">{t.allBranches}</MenuItem>
            {costCenters.map((cc) => (
              <MenuItem key={cc.costCenterId} value={cc.costCenterId}>
                {isRTL ? cc.costCenterNameAr : cc.costCenterName}
              </MenuItem>
            ))}
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.fromDate}
              value={fromDate ? new Date(fromDate) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFromDate(newValue);
                if (toDate && newValue && toDate < newValue) setToDate('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={minDate}
              maxDate={toDate ? new Date(toDate) : maxDate}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.toDate}
              value={toDate ? new Date(toDate) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setToDate(newValue);
                if (fromDate && newValue && fromDate > newValue) setFromDate('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={fromDate ? new Date(fromDate) : minDate}
              maxDate={maxDate}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
      </Paper>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress sx={{ color: '#FF6B35' }} />
        </Box>
      )}

      {/* Report Table */}
      {!loading && reportData && (
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#4A90E2', color: 'white', fontWeight: 'bold', minWidth: 100 }}>{t.date}</TableCell>
                  <TableCell sx={{ bgcolor: '#4A90E2', color: 'white', fontWeight: 'bold', minWidth: 120 }}>{t.entryNo}</TableCell>
                  <TableCell sx={{ bgcolor: '#4A90E2', color: 'white', fontWeight: 'bold', minWidth: 300 }}>{t.description}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#4CAF50', color: 'white', fontWeight: 'bold', minWidth: 120 }}>{t.debit}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#F44336', color: 'white', fontWeight: 'bold', minWidth: 120 }}>{t.credit}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#FF9800', color: 'white', fontWeight: 'bold', minWidth: 120 }}>{t.balance}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Opening Balance */}
                <TableRow sx={{ bgcolor: '#424242' }}>
                  <TableCell sx={{ color: 'white' }}></TableCell>
                  <TableCell sx={{ color: 'white' }}></TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.openingBalance}</TableCell>
                  <TableCell sx={{ color: 'white' }}></TableCell>
                  <TableCell sx={{ color: 'white' }}></TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {formatCurrency(reportData.openingBalance)}
                  </TableCell>
                </TableRow>

                {/* Details */}
                {reportData.details.map((detail, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ '@media print': { color: '#000 !important' } }}>
                      {formatDate(detail.date)}
                    </TableCell>
                    <TableCell sx={{ '@media print': { color: '#000 !important' } }}>
                      {detail.entryNo}
                    </TableCell>
                    <TableCell sx={{ '@media print': { color: '#000 !important' } }}>
                      {detail.description}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#4CAF50', fontWeight: 600, '@media print': { color: '#000 !important' } }}>
                      {detail.debit > 0 ? formatCurrency(detail.debit) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#F44336', fontWeight: 600, '@media print': { color: '#000 !important' } }}>
                      {detail.credit > 0 ? formatCurrency(detail.credit) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, '@media print': { color: '#000 !important' } }}>
                      {formatCurrency(detail.balance)}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Closing Balance */}
                <TableRow sx={{ bgcolor: '#FF9800' }}>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#000' }}>{t.closingBalance}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#000' }}>
                    {formatCurrency(reportData.totalDebit)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#000' }}>
                    {formatCurrency(reportData.totalCredit)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#000' }}>
                    {formatCurrency(reportData.closingBalance)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* No Data */}
      {!loading && !reportData && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">{t.noData}</Typography>
        </Paper>
      )}

    </Box>
  );
};

export default AccountStatementReport;